import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Gauge, TrendingUp, LayoutGrid, ArrowUpRight } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

const TONE = { emerald: "bg-emerald-500", green: "bg-green-500", slate: "bg-slate-400", amber: "bg-amber-500", red: "bg-red-500" };
const RING = {
  emerald: "border-emerald-200 hover:border-emerald-300", green: "border-green-200 hover:border-green-300",
  slate: "border-slate-200 hover:border-slate-300", amber: "border-amber-200 hover:border-amber-300",
  red: "border-red-200 hover:border-red-300",
};
const CHIP = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700", green: "border-green-200 bg-green-50 text-green-700",
  slate: "border-slate-200 bg-slate-50 text-slate-600", amber: "border-amber-200 bg-amber-50 text-amber-700",
  red: "border-red-200 bg-red-50 text-red-700",
};

export default function ImpactHeatmap() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/portal/impact-overview`).then(({ data }) => setData(data)).catch(() => setData({ companies: [], avg_score: 0, count: 0 }));
  }, []);

  return (
    <AppShell title="Your Impact" subtitle="How helpful you are across every company you back - colour-coded by your impact score">
      {!data ? (
        <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-6" data-testid="impact-heatmap-page">
          <div className="flex items-center gap-3">
            <Card className="rounded-xl border p-4"><div className="flex items-center gap-2 text-2xl font-semibold"><Gauge className="h-5 w-5 text-primary" />{data.avg_score}</div><div className="text-xs text-muted-foreground">Your avg across {data.count} companies</div></Card>
          </div>
          {data.companies.length === 0 ? (
            <Card className="rounded-xl border p-12 text-center text-muted-foreground"><LayoutGrid className="mx-auto mb-2 h-8 w-8" />No companies yet.</Card>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.companies.map((c) => (
                <Link key={c.company_id} to={`/portal/company/${c.company_id}`} className={`block rounded-xl border-2 bg-card p-4 transition-colors ${RING[c.band.tone]}`} data-testid={`heatmap-card-${c.company_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">{c.company_name}</div>
                      <div className="text-[11px] capitalize text-muted-foreground">{c.stage} stage</div>
                    </div>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-2xl font-bold text-foreground" data-testid={`heatmap-score-${c.company_id}`}>{c.score}</span>
                    <Badge variant="outline" className={CHIP[c.band.tone]}>{c.band.label}</Badge>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div className={`h-full rounded-full ${TONE[c.band.tone]}`} style={{ width: `${c.score}%` }} />
                  </div>
                  {c.projected_effects.length > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-[11px] text-emerald-700"><TrendingUp className="h-3 w-3" /> {c.projected_effects[0].text}</div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
